import type {
  CheckoutRequest,
  OrderDto,
  ProductCardDto,
  ProductDto,
  QuoteResponse,
  SlotDayDto,
} from '@telestore/contracts';
import type { ResolvedTheme } from '@telestore/theming';

/**
 * API client.
 *
 * §7 specifies typed RPC via `hono/client`, and the contracts package is the
 * single source of truth for the shapes. This wrapper keeps the fetch details
 * (base URL, locale header, error unwrapping) in one place so components never
 * touch `fetch` directly.
 */

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let locale: 'km' | 'en' = 'km';
export function setApiLocale(next: 'km' | 'en') {
  locale = next;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      'x-locale': locale,
      ...(init.headers ?? {}),
    },
  });

  if (!response.ok) {
    let code = 'internal';
    let message = 'Something went wrong';
    let details: Record<string, unknown> | undefined;
    try {
      const body = (await response.json()) as {
        error?: { code: string; message: string; details?: Record<string, unknown> };
      };
      if (body.error) {
        code = body.error.code;
        // The API already localises this string, so show it as-is.
        message = body.error.message;
        details = body.error.details;
      }
    } catch {
      message = `${response.status} ${response.statusText}`;
    }
    throw new ApiError(code, message, details, response.status);
  }

  return (await response.json()) as T;
}

/* ------------------------------------------------------------------ */
/* Shapes returned by the storefront routes                            */
/* ------------------------------------------------------------------ */

export interface ShopPayload {
  shop: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    vertical: 'retail' | 'fnb' | 'services' | 'digital';
    currency: 'USD' | 'KHR';
    defaultLocale: 'km' | 'en';
    theme: { template: string; logo: string | null; cover: string | null };
    contactTelegramUsername: string | null;
    contactPhone: string | null;
    openingHours: Array<{ weekday: number; open: string; close: string; closed: boolean }>;
    returnPolicy: { km: string; en: string };
    codEnabled: boolean;
    prepaidEnabled: boolean;
    botUsername: string | null;
    exchangeRateUsdKhr: number;
    /** §6.7: a suspended shop resolves but does not sell. */
    status: 'active' | 'suspended' | 'closed';
    suspendedReason: string | null;
  };
  resolvedTheme: ResolvedTheme;
  redirectedFrom: string | null;
}

export interface ShippingPayload {
  currency: 'USD' | 'KHR';
  provinces: Array<{
    code: string;
    en: string;
    km: string;
    price: { amount: number; currency: 'USD' | 'KHR' };
    freeAboveAmount: number | null;
    etaText: { km: string; en: string } | null;
  }>;
}

export interface PaymentPayload {
  id: string;
  provider: string;
  qrPayload: string | null;
  deepLink: string | null;
  amount: { amount: number; currency: 'USD' | 'KHR' };
  expiresAt: string | null;
  payeeLabel: string | null;
}

export interface CheckoutResponse {
  order: OrderDto;
  payment: PaymentPayload | null;
  botDeepLink: string | null;
}

export interface ReplyKitPayload {
  productUrl: string;
  shortText: { km: string; en: string };
  fullText: { km: string; en: string };
}

export const api = {
  shop: (slug: string) => request<ShopPayload>(`/storefront/${slug}`),

  categories: (slug: string) =>
    request<{ categories: Array<{ id: string; name: { km: string; en: string }; slug: string }> }>(
      `/storefront/${slug}/categories`,
    ),

  products: (slug: string, params: { categoryId?: string; q?: string; cursor?: string }) => {
    const query = new URLSearchParams();
    if (params.categoryId) query.set('categoryId', params.categoryId);
    if (params.q) query.set('q', params.q);
    if (params.cursor) query.set('cursor', params.cursor);
    return request<{ items: ProductCardDto[]; nextCursor: string | null }>(
      `/storefront/${slug}/products?${query.toString()}`,
    );
  },

  product: (slug: string, productSlug: string) =>
    request<{ product: ProductDto; og: unknown; replyKit: ReplyKitPayload }>(
      `/storefront/${slug}/products/${productSlug}`,
    ),

  shipping: (slug: string) => request<ShippingPayload>(`/storefront/${slug}/shipping`),

  /**
   * §4 booking: free slots for one service on one day.
   *
   * Public, like the rest of this file — a customer arriving from a Facebook
   * ad has to be able to see that Thursday 3pm is open before we ask them for
   * anything (§6.3).
   */
  slots: (slug: string, params: { productId: string; date: string; resourceId?: string }) => {
    const query = new URLSearchParams({ productId: params.productId, date: params.date });
    if (params.resourceId) query.set('resourceId', params.resourceId);
    return request<SlotDayDto>(`/storefront/${slug}/slots?${query.toString()}`);
  },

  /** §6.7 report mechanism — no account, no login, no friction. */
  report: (body: {
    shopSlug: string;
    productId?: string | null;
    reason: string;
    detail?: string;
    contact?: string;
  }) =>
    request<{ received: boolean }>(`/public/reports`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  reportOptions: () =>
    request<{
      reportReasons: Array<{ id: string; label: { km: string; en: string } }>;
    }>(`/public/policy`),

  quote: (
    slug: string,
    body: {
      lines: CheckoutRequest['lines'];
      provinceCode?: string;
      promotionCodes?: string[];
      /** §4: the same shape checkout sends, so the quote prices what is charged. */
      fulfilment?: CheckoutRequest['fulfilment'];
    },
  ) =>
    request<QuoteResponse>(`/storefront/${slug}/quote`, {
      method: 'POST',
      body: JSON.stringify({ ...body, paymentType: 'prepaid' }),
    }),

  checkout: (slug: string, body: CheckoutRequest) =>
    request<CheckoutResponse>(`/storefront/${slug}/checkout`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  order: (slug: string, code: string) =>
    request<{ order: OrderDto; payment: PaymentPayload | null }>(
      `/storefront/${slug}/orders/${code}`,
    ),

  /**
   * Who is opening this Mini App. Called once on start so a buyer arriving
   * through the shop's bot is a known customer from their first screen rather
   * than from their first order.
   */
  identifyTelegram: (slug: string, initData: string) =>
    request<{ customer: { id: string; name: string | null; phone: string | null } }>(
      `/storefront/${slug}/telegram/identify`,
      { method: 'POST', body: JSON.stringify({ initData }) },
    ),

  linkTelegram: (slug: string, code: string, initData: string) =>
    request<{ linked: boolean }>(`/storefront/${slug}/orders/${code}/link-telegram`, {
      method: 'POST',
      body: JSON.stringify({ initData }),
    }),

  /** Live order updates — the payment page flips the instant a transfer lands. */
  orderStream: (slug: string, code: string) => new EventSource(`${BASE}/storefront/${slug}/orders/${code}/stream`),
};
